import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import useAuth from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import NewClientEngagementModal from "./NewClientEngagementModal";
import styles from "./Sidebar.module.css";

import adminAvatar from "../assets/Adminavatar.jpg";
import logoutDark from "../assets/logout.avif";
import logoutLight from "../assets/logout-white.avif";

interface SidebarProps {
  activeKey?: "dashboard" | "itaxquest" | "eci" | "formc" | "feedback" | "notifications" | "alerts" | "home";
  compact?: boolean;
  /**
   * Pre-authentication mode (login / role-select screens). Hides the iTaxComp
   * item — which starts a new engagement via an authenticated API and only
   * makes sense once logged in — and points Home at the Welcome/role-select
   * page instead of a dashboard that would just bounce back to login.
   */
  preAuth?: boolean;
}

export const SIDEBAR_WIDTH_FULL = 240;
// 110 matches Figma's icon rail (measured edge at x≈110 CSS px). Shared with
// the Preparer dashboard, which uses the same compact rail.
export const SIDEBAR_WIDTH_COMPACT = 110;

const ENGAGEMENT_BLOCK_CODES = new Set(["QUOTA_EXCEEDED", "TRIAL_EXPIRED", "CSP_DISABLED"]);

const getEngagementCreateErrorMessage = (err: any): string => {
  const code = err?.response?.data?.code;
  const message = err?.response?.data?.message;
  if (code && ENGAGEMENT_BLOCK_CODES.has(code) && message) {
    return message;
  }
  return "Failed to create case.";
};

export default function Sidebar({ activeKey = "dashboard", compact = true, preAuth = false }: SidebarProps) {
  const navigate = useNavigate();
  const [openCompMenu, setOpenCompMenu] = useState(activeKey === "eci" || activeKey === "formc");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { user, logout } = useAuth();
  // The Logout button used to `navigate("/login")` and nothing else — it
  // showed the login page while leaving the session completely intact, which
  // is why pressing Back landed straight back on the dashboard. Go through
  // the real logout, which clears the tokens and ends the session server-side.
  const handleLogout = () => {
    if (isMobile) setSidebarOpen(false);
    logout();
  };
  const location = useLocation();
  const [showDashboardConfirm, setShowDashboardConfirm] = useState(false);
  const { theme } = useTheme();
  const [showEngagementModal, setShowEngagementModal] = useState(false);

  const getInitials = (name?: string) => {
    if (!name) return "AD";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };
  // The email is the identifier users actually recognise, and unlike `name`
  // (cached at login, so it can go stale) it always matches the account they
  // signed in with — so derive the avatar from it and keep the name as a
  // fallback for accounts stored without one.
  const getEmailInitials = (email?: string) => {
    const local = email?.trim().split("@")[0]?.replace(/[^a-zA-Z0-9]/g, "");
    return local ? local.slice(0, 2).toUpperCase() : "";
  };

  // On pre-auth screens (e.g. the Approver Login page) nobody is signed in
  // for this flow yet, so never surface a persisted previous session's
  // initials here — that both looks wrong and leaks the prior user's identity.
  const userInitials = preAuth
    ? ""
    : getEmailInitials(user?.email) || getInitials(user?.name || "Admin");

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpenCompMenu(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleStartEci = async () => {
    try {
      const res = await api.post("/engagement", {
        clientId: 1,
        yearOfAssessment: 2026,
        formType: "ECI",
      });
      const engagementId = res.data?.data?.id;
      if (!engagementId) throw new Error("Engagement ID not returned");
      navigate(`/comp/${engagementId}`);
    } catch (err) {
      console.error("Failed to start ECI", err);
      alert("Unable to start ECI. Please try again.");
    }
  };

  const handleStartFormC = async () => {
    try {
      const res = await api.post("/engagement", {
        clientId: 1,
        yearOfAssessment: 2026,
        formType: "FORM_C",
      });
      const engagementId = res.data?.data?.id;
      if (!engagementId) throw new Error("Engagement ID not returned");
      navigate(`/comp/${engagementId}`);
    } catch (err) {
      console.error("Failed to start Form C", err);
      alert("Unable to start Form C. Please try again.");
    }
  };

  const openNewCaseModal = async () => {
    try {
      await api.get("/engagement/eligibility");
      setShowEngagementModal(true);
    } catch (err) {
      alert(getEngagementCreateErrorMessage(err));
    }
  };

  const handleCreateEngagement = async (
    companyName: string,
    taxRef: string,
    functionalCurrency: string,
    yearOfAssessment: string,
    basisPeriodStart: string,
    basisPeriodEnd: string,
    exchangeRate?: string
  ) => {
    setShowEngagementModal(false);
    try {
      const ya = Number(yearOfAssessment) || new Date().getFullYear();
      const payload = {
        clientId: 1,
        yearOfAssessment: ya,
        formType: "ECI",
      };
      const res = await api.post("/engagement", payload);
      const engagementId = res.data?.data?.id;
      if (!engagementId) throw new Error("Engagement ID not returned");

      try {
        await api.put(`/engagement/${engagementId}/client-profile`, {
          entityName: companyName,
          uen: taxRef,
          yearOfAssessment: ya,
          functionalCurrency: functionalCurrency || "SGD",
          basisPeriodStart,
          basisPeriodEnd,
          // Manual rate for a custom currency only; blank lets the backend
          // resolve the rate from MAS for predefined currencies.
          ...(exchangeRate ? { exchangeRate: Number(exchangeRate) } : {}),
        });
      } catch (profileErr) {
        console.warn("[Engagement] Failed to save client profile (non-fatal):", profileErr);
      }

      navigate(`/comp/${engagementId}`);
    } catch (err) {
      console.error("Failed to create engagement", err);
      alert(getEngagementCreateErrorMessage(err));
    }
  };

  // Approver vs preparer nav. Primary signal is the dashboard the user is
  // actually working from ("itax:lastDashboard", set by the Approver/Preparer
  // pages) — this is deterministic even when the backend role isn't a clean
  // lowercase "approver". Falls back to the stored role / roles[] / email so
  // it still works before either dashboard has been visited.
  const isApprover = (() => {
    const lastDashboard =
      typeof window !== "undefined" ? localStorage.getItem("itax:lastDashboard") : null;
    if (lastDashboard === "/approver") return true;
    if (lastDashboard === "/preparer") return false;
    const roleStr = String((user as any)?.role ?? "").toLowerCase();
    if (roleStr) return roleStr === "approver";
    const rolesArr: string[] = Array.isArray((user as any)?.roles)
      ? (user as any).roles.map((r: any) => String(r).toLowerCase())
      : [];
    if (rolesArr.length) return rolesArr.some((r) => r.includes("approver"));
    return `${user?.name ?? ""} ${user?.email ?? ""}`.toLowerCase().includes("approver");
  })();
  const dashboardPath = isApprover ? "/approver" : "/preparer";
  const isOnDashboard = location.pathname === dashboardPath;

  const handleLogoClick = () => {
    if (isOnDashboard) return;
    setShowDashboardConfirm(true);
  };

  const confirmRedirectToDashboard = () => {
    setShowDashboardConfirm(false);
    navigate(user?.role === "approver" ? "/approver" : "/preparer");
  };

  const cancelRedirect = () => setShowDashboardConfirm(false);

  // Legal document links — the source docs live as static files under
  // public/legal and are opened in a new tab so they render exactly as
  // authored (Word/HTML export), fully isolated from the app's styles.
  const LegalLinks = ({ stacked, isCompact }: { stacked?: boolean; isCompact?: boolean }) => (
    <div className={isCompact ? styles.legalLinksCompact : styles.legalLinksFull}>
      <a
        href="/legal/terms-of-use.html"
        target="_blank"
        rel="noopener noreferrer"
        title="Terms of Use"
        className={isCompact ? styles.legalLinkCompact : styles.legalLinkFull}
      >
        Terms of Use
      </a>
      {!stacked && <span className={styles.legalDot}>·</span>}
      <a
        href="/legal/privacy-notice.html"
        target="_blank"
        rel="noopener noreferrer"
        title="Privacy Notice"
        className={isCompact ? styles.legalLinkCompact : styles.legalLinkFull}
      >
        Privacy Notice
      </a>
    </div>
  );

  // Nav item — compact shows icon + label stacked, full shows icon + label inline
  const NavItem = ({
    icon,
    label,
    active,
    onClick,
    title,
  }: {
    icon: string;
    label: string;
    active: boolean;
    onClick: () => void;
    title?: string;
  }) => {
    if (compact) {
      return (
        <div
          title={title || label}
          onClick={onClick}
          className={
            active
              ? `${styles.navItemCompact} ${styles.navItemCompactActive}`
              : styles.navItemCompact
          }
        >
          <img
            src={icon}
            className={styles.navItemCompactIcon}
            alt={label}
          />
        </div>
      );
    }

    // Full mode — icon + label inline
    return (
      <div
        title={title || label}
        onClick={onClick}
        className={
          active
            ? `${styles.navItemFull} ${styles.navItemFullActive}`
            : styles.navItemFull
        }
      >
        <img
          src={icon}
          className={styles.navItemFullIcon}
          alt={label}
        />
        {label}
      </div>
    );
  };

  return (
    <>
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className={styles.menuToggle}
        >
          ☰
        </button>
      )}

      <aside
        className={`${styles.sidebar} ${compact ? styles.sidebarCompact : styles.sidebarFull
          } ${sidebarOpen ? styles.sidebarOpen : ""}`.trim()}
      >
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className={styles.closeButton}
          >
            ✕
          </button>
        )}

        {/* LOGO */}
        <div
          className={`${styles.logoWrapper} ${compact ? styles.logoWrapperCompact : styles.logoWrapperFull
            }`}
        >
          <img
            src="/itaxlogo3.png"
            alt="logo"
            className={`${styles.logoImg} ${compact ? styles.logoImgCompact : styles.logoImgFull
              }`}
            onClick={handleLogoClick}
          />
        </div>

        {/* NAV */}
        <nav
          className={`${styles.navContainer} ${compact ? styles.navContainerCompact : styles.navContainerFull
            }`}
        >
          {isApprover ? (
            // Approver nav
            <>
              <NavItem
                icon={process.env.PUBLIC_URL + "/home_nav.png"}
                label="Home"
                active={activeKey === "dashboard" || activeKey === "home"}
                onClick={() => { navigate(preAuth ? "/welcome" : "/approver"); if (isMobile) setSidebarOpen(false); }}
              />
              {!preAuth && (
                <NavItem
                  icon={process.env.PUBLIC_URL + "/iTaxComp_nav.png"}
                  label="iTax"
                  active={activeKey === "eci" || activeKey === "formc"}
                  onClick={() => { navigate("/welcome"); if (isMobile) setSidebarOpen(false); }}
                />
              )}
              <NavItem
                icon={process.env.PUBLIC_URL + "/iTaxQuest_nav.png"}
                label="Quest"
                active={activeKey === "itaxquest"}
                onClick={() => { navigate("/itaxquest-approver"); if (isMobile) setSidebarOpen(false); }}
              />
              <NavItem
                icon={process.env.PUBLIC_URL + "/Feedback_nav.png"}
                label="Feedback"
                active={activeKey === "feedback"}
                onClick={() => { navigate("/feedback-approver"); if (isMobile) setSidebarOpen(false); }}
              />
            </>
          ) : (
            <>
              <NavItem
                icon={process.env.PUBLIC_URL + "/home_nav.png"}
                label="Home"
                active={activeKey === "dashboard" || activeKey === "home"}
                onClick={() => { navigate(preAuth ? "/welcome" : "/preparer"); if (isMobile) setSidebarOpen(false); }}
              />
              {!preAuth && (
                <NavItem
                  icon={process.env.PUBLIC_URL + "/iTaxComp_nav.png"}
                  label="iTax"
                  active={activeKey === "eci" || activeKey === "formc"}
                  onClick={() => { openNewCaseModal(); if (isMobile) setSidebarOpen(false); }}
                />
              )}
              <NavItem
                icon={process.env.PUBLIC_URL + "/iTaxQuest_nav.png"}
                label="Quest"
                active={activeKey === "itaxquest"}
                onClick={() => { navigate("/itaxquest"); if (isMobile) setSidebarOpen(false); }}
              />
              <NavItem
                icon={process.env.PUBLIC_URL + "/Feedback_nav.png"}
                label="Feedback"
                active={activeKey === "feedback"}
                onClick={() => { navigate("/feedback"); if (isMobile) setSidebarOpen(false); }}
              />
            </>
          )}
        </nav>

        {/* BOTTOM — avatar + logout */}
        {compact ? (
          <div className={styles.bottomCompact}>
            {/* Placeholder only — the initials identify the signed-in user and
                are not a control; logging out is the button below. */}
            <div
              title={preAuth ? "Not signed in" : `${user?.name || "Admin"} · ${user?.email || "enquiry@itax.sg"}`}
              className={styles.avatarInitials}
            >
              {preAuth ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="8" r="4" fill="currentColor" />
                  <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" fill="currentColor" />
                </svg>
              ) : (
                userInitials
              )}
            </div>
            <button
              onClick={handleLogout}
              className={styles.compactLogoutButton}
              title="Logout"
            >
              <span className={styles.compactLogoutText}>Logout</span>
            </button>
            <LegalLinks stacked isCompact />
          </div>
        ) : (
          <div className={styles.bottomFull}>
            <img src={adminAvatar} className={styles.fullAvatar} alt="Admin Avatar" />
            <div className={styles.fullUserInfo}>
              <div className={styles.fullUserName}>Admin</div>
              <div className={styles.fullUserEmail}>enquiry@itax.sg</div>
            </div>
            <button
              onClick={handleLogout}
              className={styles.fullLogoutButton}
              title="Logout"
            >
              <img
                src={theme === "dark" ? logoutDark : logoutLight}
                className={styles.fullLogoutIcon}
                alt="Logout"
              />
              <span>Logout</span>
            </button>
            <LegalLinks />
          </div>
        )}
      </aside>

      {showEngagementModal && (
        <div className={styles.modalBackdrop}>
          <NewClientEngagementModal
            onCancel={() => setShowEngagementModal(false)}
            onCreateEngagement={handleCreateEngagement}
          />
        </div>
      )}

      {/* Dashboard redirect confirm */}
      {showDashboardConfirm && (
        <div
          onClick={cancelRedirect}
          className={styles.confirmBackdrop}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={styles.confirmCard}
          >
            <div className={styles.confirmText}>
              You will be redirected to your dashboard. Do you want to continue?
            </div>
            <div className={styles.confirmButtonRow}>
              <button
                onClick={confirmRedirectToDashboard}
                className={styles.confirmYesButton}
              >
                Yes
              </button>
              <button
                onClick={cancelRedirect}
                className={styles.confirmNoButton}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

